import { debounce, requestIdleCallback, viewTransition } from '@theme/utilities';

const OFFSET = 40;

/**
 * R77: the breakpoint at which these panels actually become floating. Below it,
 * `blocks/filters.liquid:894` scopes `position: absolute` (and the popover chrome) inside
 * `@media screen and (width >= 750px)`, so a sub-750px panel is static, in flow, and has
 * nothing to reposition. Keep this value in sync with that media query.
 */
const DESKTOP_PANEL_QUERY = '(min-width: 750px)';

/**
 * A custom element that manages a floating panel.
 */
export class FloatingPanelComponent extends HTMLElement {
  #desktop = matchMedia(DESKTOP_PANEL_QUERY);
  #details = /** @type {HTMLDetailsElement | null} */ (null);

  #updatePosition = async () => {
    // R77 guard 1: hard no-op below 750px. Measured on a throttled mobile collection trace,
    // 17 of these components exist and ALL 17 are 0x0 with a hidden ancestor, yet each one
    // read geometry and wrote styles on idle -- 261-281 ms of forced reflow, the largest and
    // most stable single culprit on the page, to nudge panels that are not on screen.
    if (!this.#desktop.matches) return;

    // R79 guard 2: gate on ACTUAL floating mode, not viewport width. `blocks/filters.liquid:894`
    // only makes `.facets--horizontal .facets__panel-content` and `.sorting-filter__options`
    // absolute, and this store renders its collection facets VERTICAL. Measured live at 1280px:
    // 16 of 17 panels are `position: static` and 8 of 8 VISIBLE panels are static, every one of
    // them carrying an inline `top: 40px` that does nothing to a static element. R77's breakpoint
    // check was necessary but not sufficient. Reading `position` costs a style resolution; the
    // rect read plus two style writes it replaces cost a layout.
    if (getComputedStyle(this).position === 'static') return;

    // R79 guard 3: use checkVisibility(), NOT geometry. A CLOSED account popover reports a real
    // 374x261 box with a non-null offsetParent at 1280px, so any rect- or ResizeObserver-based
    // test fires while it is visually hidden. Where the API is unavailable, fall back to the
    // SEMANTIC state -- the parent <details> -- rather than silently running unguarded, which is
    // what R77 did.
    if (!this.#isVisible()) return;

    // Wait for any view transitions to finish
    if (viewTransition.current) await viewTransition.current;

    // R79: re-validate after the await. The panel can be closed, disconnected, morphed or dropped
    // below the breakpoint while the transition resolves, and the old code would measure and write
    // to it regardless.
    if (!this.isConnected || !this.#desktop.matches) return;
    if (getComputedStyle(this).position === 'static') return;
    if (!this.#isVisible()) return;

    // R79: rebase before measuring. This method only ever SET `left`, never cleared it, so once a
    // panel was nudged away from an edge it kept that correction for the life of the page -- even
    // after a resize that removed the overflow entirely. Latent before R77; R77 added the resize
    // and breakpoint re-entry paths that made it reachable.
    this.style.left = '';

    const rect = this.getBoundingClientRect();
    const viewportWidth = window.innerWidth;

    this.style.top = OFFSET + 'px';

    if (rect.right > viewportWidth) {
      const overflowAmount = rect.right - viewportWidth + OFFSET;
      this.style.left = `-${overflowAmount}px`;
    }

    if (rect.left < 0) {
      const overflowAmount = Math.abs(rect.left) + OFFSET;
      this.style.left = `${overflowAmount}px`;
    }
  };

  /**
   * R79: visibility with an EXPLICIT fallback. `checkVisibility()` is the reliable signal, but
   * where it is unavailable we use the parent `<details>` open state rather than no guard at all.
   * We deliberately do NOT fall back to `offsetParent` or a rect test: a closed account popover
   * reports a real 374x261 box with a non-null offsetParent, so geometry lies here.
   */
  #isVisible() {
    if (typeof this.checkVisibility === 'function') return this.checkVisibility();
    if (this.#details) return this.#details.open;
    return true;
  }

  /**
   * R77: this is the re-arm point. The old code positioned every panel once on idle and then
   * relied on a MutationObserver bound to THIS element's own attributes -- which observed the
   * `style.top` / `style.left` writes `#updatePosition` had just made, and nothing else. The
   * event that actually corresponds to a panel becoming visible is `toggle` on its
   * `<details>`, so listen for that and position on the next frame, once layout has settled.
   */
  #handleToggle = () => {
    if (!this.#details?.open) return;
    requestAnimationFrame(() => this.#updatePosition());
  };

  #resizeListener = debounce(() => {
    const parent = this.#details;
    // NOTE: `data-close-on-resize` is rendered as a BARE attribute by list-filter.liquid:113
    // and price-filter.liquid:81, so `dataset.closeOnResize` is '' and this comparison has
    // never once been true. Left exactly as written -- making it fire would start closing
    // filter panels on resize, which is a UX change and belongs in its own slice, not here.
    const closeOnResize = this.dataset.closeOnResize === 'true';
    if (parent instanceof HTMLDetailsElement && closeOnResize) {
      parent.open = false;
      parent.removeAttribute('open');
      this.#updatePosition();
      return;
    }

    // R77: panels WITHOUT close-on-resize (the account popover and the sorting panel) still
    // need repositioning if the window is resized while they are open.
    if (parent?.open) this.#updatePosition();
  }, 100);

  /**
   * R77: crossing the 750px boundary is the one case where a panel goes from "never needs
   * positioning" to "needs positioning" without a toggle firing.
   */
  #handleBreakpointChange = () => {
    if (this.#desktop.matches && this.#details?.open) this.#updatePosition();
  };

  connectedCallback() {
    this.#details = this.closest('details');

    window.addEventListener('resize', this.#resizeListener);
    this.#desktop.addEventListener('change', this.#handleBreakpointChange);
    this.#details?.addEventListener('toggle', this.#handleToggle);

    requestIdleCallback(() => {
      // A panel inside a closed <details> is positioned by #handleToggle when it opens.
      if (this.#details && !this.#details.open) return;
      this.#updatePosition();
    });
  }

  disconnectedCallback() {
    window.removeEventListener('resize', this.#resizeListener);
    this.#desktop.removeEventListener('change', this.#handleBreakpointChange);
    this.#details?.removeEventListener('toggle', this.#handleToggle);
  }
}

if (!customElements.get('floating-panel-component')) {
  customElements.define('floating-panel-component', FloatingPanelComponent);
}
